
import { useState, useEffect } from 'react';
import CustomDatePicker from './CustomDatePicker';
import GuestSelector from './GuestSelector';
import { useSearchState } from '@/hooks/useSearchState';

interface SearchFormBookingProps {
  checkIn?: Date;
  checkOut?: Date;
  guests: string;
  onCheckInChange: (date: Date | undefined) => void;
  onCheckOutChange: (date: Date | undefined) => void;
  onGuestsChange: (value: string) => void;
  maxGuests?: number;
  unavailableDates?: Date[];
}

const SearchFormBooking = ({
  checkIn,
  checkOut,
  guests,
  onCheckInChange,
  onCheckOutChange,
  onGuestsChange,
  maxGuests = 6,
  unavailableDates = []
}: SearchFormBookingProps) => {
  const [checkOutPickerOpen, setCheckOutPickerOpen] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const searchState = useSearchState(checkIn, checkOut, guests);

  // Only initialize from URL on first mount, don't continuously sync
  useEffect(() => {
    if (hasInitialized) return;

    console.log('🎯 SearchFormBooking initial URL sync');
    searchState.updateFromUrlParams();
    
    // Sync with parent component
    if (searchState.checkIn !== checkIn) {
      onCheckInChange(searchState.checkIn);
    }
    if (searchState.checkOut !== checkOut) {
      onCheckOutChange(searchState.checkOut);
    }
    if (searchState.guests !== guests) {
      onGuestsChange(searchState.guests);
    }

    setHasInitialized(true);
  }, [hasInitialized, searchState, onCheckInChange, onCheckOutChange, onGuestsChange, checkIn, checkOut, guests]);

  const handleCheckInChange = (date: Date | undefined) => {
    console.log('📅 SearchFormBooking check-in changed:', date);
    onCheckInChange(date);
    
    // If check-out is selected and is before or equal to the new check-in, clear it
    if (date && checkOut && checkOut <= date) {
      onCheckOutChange(undefined);
    }
  };

  const handleCheckOutChange = (date: Date | undefined) => {
    console.log('📅 SearchFormBooking check-out changed:', date);
    onCheckOutChange(date);
  };

  const handleAutoOpenCheckOut = () => {
    setCheckOutPickerOpen(true);
  };

  console.log('🎯 SearchFormBooking render with:', { checkIn, checkOut, guests });

  return (
    <div className="space-y-4">
      {/* Fechas en una fila */}
      <div className="grid grid-cols-2 border border-gray-300 rounded-lg overflow-hidden">
        <div className="p-3 border-r border-gray-300">
          <div className="text-xs font-semibold text-gray-700 mb-1">LLEGADA</div>
          <CustomDatePicker 
            selected={checkIn} 
            onSelect={handleCheckInChange} 
            placeholder="10/7/2025"
            className="border-0 p-0 h-auto bg-transparent text-sm"
            unavailableDates={unavailableDates}
            minDate={new Date()} // No permitir fechas pasadas
            autoOpenNext={handleAutoOpenCheckOut}
          />
        </div>
        <div className="p-3">
          <div className="text-xs font-semibold text-gray-700 mb-1">SALIDA</div>
          <CustomDatePicker 
            selected={checkOut} 
            onSelect={handleCheckOutChange} 
            placeholder="11/7/2025"
            className="border-0 p-0 h-auto bg-transparent text-sm"
            unavailableDates={unavailableDates}
            minDate={checkIn ? new Date(checkIn.getTime() + 24 * 60 * 60 * 1000) : new Date()} // Mínimo un día después de check-in
            open={checkOutPickerOpen}
            onOpenChange={setCheckOutPickerOpen}
          />
        </div>
      </div>

      {/* Huéspedes */}
      <div className="border border-gray-300 rounded-lg p-3">
        <div className="text-xs font-semibold text-gray-700 mb-1">HUÉSPEDES</div>
        <GuestSelector
          value={guests}
          onChange={onGuestsChange}
          maxGuests={maxGuests}
          className="border-0 p-0 h-auto bg-transparent text-sm"
          placeholder="1 huésped"
        />
      </div>
    </div>
  );
};

export default SearchFormBooking;
